import React, { useEffect, useState } from 'react';
import Modal from '@/ui/modal/Modal';
import OrderEventController from '@/services/OrderEventController';
import { ScoreBoard } from '@/components/settings/auto-assign/ScoreBoard';
import { formatClock } from './activityLabels';
import { TRIGGER_TEXT, decisionToRows, orderContextFor } from './decisionRows';

// "Why that rider?", answered on the live board rather than only in the
// settings simulator.
//
// The board here is the SAME component the auto-assign page uses — see
// decisionRows.js for why the stored decision is reshaped rather than the board
// taught a second format. What was a rehearsal in settings becomes the record of
// a real decision here, and the two cannot drift.
export const ActivityDecisionModal = ({ orderId, allDeliveries, onClose }) => {
	const [state, setState] = useState({ status: 'loading', decisions: [] });

	useEffect(() => {
		let cancelled = false;

		OrderEventController.getDecisions(orderId).then(res => {
			if (cancelled) {
				return;
			}
			setState(
				res?.error
					? { status: 'error', decisions: [] }
					: { status: 'ready', decisions: res.decisions || [] },
			);
		});

		return () => {
			cancelled = true;
		};
	}, [orderId]);

	return (
		<Modal title={`Γιατί επιλέχθηκε — παραγγελία #${orderId}`} onClose={onClose}>
			<div className='flex flex-col gap-y-3 p-1'>
				{state.status === 'loading' && (
					<div className='text-[13px]/[17px] text-slate-400'>Φόρτωση…</div>
				)}

				{state.status === 'error' && (
					<div className='text-[13px]/[17px] text-red-700'>Δεν φορτώθηκε η αιτιολόγηση.</div>
				)}

				{state.status === 'ready' && !state.decisions.length && (
					<div className='text-[13px]/[17px] text-slate-500'>
						Δεν υπάρχει καταγεγραμμένη αυτόματη απόφαση για αυτή την παραγγελία. Οι χειροκίνητες
						αναθέσεις και όσες πήραν οι διανομείς μόνοι τους δεν έχουν βαθμολογία — τις αποφάσισε
						άνθρωπος.
					</div>
				)}

				{/* An order that went round twice has more than one. Newest first, which
				    is the one that stuck. */}
				{state.decisions.map(decision => {
					const rows = decisionToRows(decision, allDeliveries);
					const context = orderContextFor(decision);

					return (
						<div key={decision.id} className='flex flex-col gap-y-1.5'>
							<div className='flex flex-row flex-wrap items-center gap-x-2 text-[12px] text-slate-500'>
								<span className='font-mono'>{formatClock(decision.createdAt)}</span>
								<span>{TRIGGER_TEXT[decision.trigger] || decision.trigger}</span>
								{decision.outcome === 'noCandidate' && (
									<span className='font-semibold text-amber-700'>κανένας διαθέσιμος</span>
								)}
								{decision.outcome === 'revoked' && (
									<span className='font-semibold text-red-700'>επιστράφηκε</span>
								)}
							</div>

							{/* Why THIS order, before any rider was scored. The board below
							    answers the other half. */}
							{!!context.length && (
								<div className='flex flex-row flex-wrap gap-2'>
									{context.map(c => (
										<div
											key={c.key}
											className='flex min-w-0 flex-col rounded-md border border-slate-200 bg-slate-50 px-2 py-1'
										>
											<span className='text-[11px] text-slate-400'>{c.label}</span>
											<span className='text-[13px] font-semibold text-slate-700'>{c.value}</span>
											{c.note && <span className='text-[11px] text-slate-400'>{c.note}</span>}
										</div>
									))}
								</div>
							)}

							{/* winnerId: who actually got it, not who the scores say should have. */}
							{rows.length ? (
								<ScoreBoard rows={rows} winnerId={decision.deliveryId} />
							) : (
								<div className='text-[12px] text-slate-400'>
									Δεν καταγράφηκαν υποψήφιοι για αυτή την απόφαση.
								</div>
							)}
						</div>
					);
				})}
			</div>
		</Modal>
	);
};

export default ActivityDecisionModal;
